import type { CustomColumnPreference } from '../../api/column-visibility-preferences';

export type CustomColumnId = 'cc1' | 'cc2' | 'cc3';
export type CustomColumnVisibilityKey = 'customColumn1' | 'customColumn2' | 'customColumn3';

export const CUSTOM_COLUMN_IDS: CustomColumnId[] = ['cc1', 'cc2', 'cc3'];

export const CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID: Record<CustomColumnId, CustomColumnVisibilityKey> = {
  cc1: 'customColumn1',
  cc2: 'customColumn2',
  cc3: 'customColumn3',
};

export const CUSTOM_COLUMN_ID_BY_VISIBILITY_KEY: Record<CustomColumnVisibilityKey, CustomColumnId> = {
  customColumn1: 'cc1',
  customColumn2: 'cc2',
  customColumn3: 'cc3',
};

export const isCustomColumnVisibilityKey = (key: string): key is CustomColumnVisibilityKey =>
  key === 'customColumn1' || key === 'customColumn2' || key === 'customColumn3';

export const getCustomColumnDefaultName = (id: CustomColumnId): string => {
  if (id === 'cc1') return 'Custom Field 1';
  if (id === 'cc2') return 'Custom Field 2';
  return 'Custom Field 3';
};

export const isConfiguredCustomColumn = (
  column: Pick<CustomColumnPreference, 'id' | 'name'>
): boolean => {
  const id = String(column?.id || '').trim() as CustomColumnId;
  if (!CUSTOM_COLUMN_IDS.includes(id)) return false;
  const name = String(column?.name || '').trim();
  if (!name) return false;
  const defaultName = getCustomColumnDefaultName(id);
  return name.toLowerCase() !== defaultName.toLowerCase();
};

const normalizeInputType = (value: unknown): 'text' | 'dropdown' =>
  String(value || '').toLowerCase() === 'dropdown' ? 'dropdown' : 'text';

const normalizeOptions = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const options: string[] = [];
  value.forEach((option) => {
    const normalized = String(option || '').trim();
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    options.push(normalized);
  });
  return options;
};

export const normalizeCustomColumnsConfig = (
  columns?: CustomColumnPreference[] | null
): CustomColumnPreference[] => {
  const byId = new Map<string, CustomColumnPreference>();
  if (Array.isArray(columns)) {
    columns.forEach((column) => {
      if (!column) return;
      const id = String(column.id || '').trim();
      if (!CUSTOM_COLUMN_IDS.includes(id as CustomColumnId)) return;
      byId.set(id, {
        id,
        name: String(column.name || '').trim(),
        inputType: normalizeInputType(column.inputType),
        options: normalizeOptions(column.options),
      });
    });
  }

  return CUSTOM_COLUMN_IDS.map((id) => {
    const found = byId.get(id);
    if (!found) {
      return {
        id,
        name: getCustomColumnDefaultName(id),
        inputType: 'text' as const,
        options: [],
      };
    }
    return {
      id,
      name: found.name || getCustomColumnDefaultName(id),
      inputType: normalizeInputType(found.inputType),
      options: normalizeOptions(found.options),
    };
  });
};

type StoredCustomColumnValue =
  | {
      id?: string;
      value?: unknown;
    }
  | string
  | null
  | undefined;

export type StoredCustomColumnsValues = Partial<Record<CustomColumnId, StoredCustomColumnValue>>;

const normalizeStoredEntry = (columnId: CustomColumnId, raw: StoredCustomColumnValue) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    return {
      id: columnId,
      value: raw,
    };
  }

  const value = raw?.value;
  if (value === null || value === undefined) return null;
  return {
    id: columnId,
    value: String(value),
  };
};

export const readCustomColumnValue = (rawValues: unknown, columnId: CustomColumnId): string => {
  if (!rawValues || typeof rawValues !== 'object') return '';
  const entry = normalizeStoredEntry(
    columnId,
    (rawValues as StoredCustomColumnsValues)[columnId]
  );
  return entry?.value ?? '';
};

export const writeCustomColumnValue = (
  rawValues: unknown,
  columnId: CustomColumnId,
  nextValue: string
): Record<string, { id: string; value: string }> | null => {
  const next: Record<string, { id: string; value: string }> = {};
  if (rawValues && typeof rawValues === 'object') {
    CUSTOM_COLUMN_IDS.forEach((id) => {
      const entry = normalizeStoredEntry(id, (rawValues as StoredCustomColumnsValues)[id]);
      if (entry && entry.value !== '') {
        next[id] = entry;
      }
    });
  }

  if (nextValue !== '') {
    next[columnId] = {
      id: columnId,
      value: nextValue,
    };
  } else {
    delete next[columnId];
  }

  return Object.keys(next).length > 0 ? next : null;
};
